import { Fragment, useEffect } from 'react';
import { ButtonPrimary, Type } from '@spotify-internal/encore-web';

import withTranslation from 'lib/shared/i18n/withTranslation';
import { TranslationProps } from 'etc/types';
import SelectField from 'components/Fields/SelectField/SelectField';
import TextField from 'components/Fields/TextField/TextField';
import RadioField from 'components/Fields/RadioField/RadioField';
import TextareaField from 'components/Fields/TextareaField/TextareaField';
import Step2ReportedContentUriAndType from 'components/ContentReporting/ContentPolicyReporting/Step2ReportedContentUriAndType';
import {
  Field,
  FieldProperties,
  OptionsTeam,
  OptionsUrgency,
  TeamOptions,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import { StepProps } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { withFullTranslationKey } from 'containers/ContentReporting/ContentPolicyReporting/utils/functions';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';

import analytics, { StepLabel } from './policy-reporting-analytics-utils';

export interface Step2EmployeeReportedContentProps extends StepProps, TranslationProps {
  fieldValue: (fieldIdentifier: string) => string;
  fieldValid: (fieldIdentifier: string) => boolean;
  loading: boolean;
}

const Step2EmployeeReportedContent = ({
  forceValidation,
  t,
  onStepSubmit,
  onFieldChange,
  fieldValue,
  fieldValid,
  loading,
  setLoading,
  location,
}: Step2EmployeeReportedContentProps): JSX.Element => {
  useEffect(
    () =>
      analytics.trackVirtualPageView(
        location.pathname,
        location.search,
        StepLabel.Step2ReportedContentSpotifyEmployees,
      ),
    [location.pathname, location.search],
  );

  return (
    <Fragment>
      <Type.h1 paddingBottom="0.67em" variant="altoBrio">
        {t('what_do_you_want_to_report')}
      </Type.h1>
      <Step2ReportedContentUriAndType
        forceValidation={forceValidation}
        onFieldChange={onFieldChange}
        fieldValue={fieldValue}
        fieldValid={fieldValid}
        setLoading={setLoading}
        isEmployee
      />
      <SelectField
        field={{
          name: Field.TEAM,
          type: 'text',
          options: TeamOptions,
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        tPrefix={withFullTranslationKey(FieldProperties[Field.TEAM].translationPrefix)}
        value={fieldValue(Field.TEAM)}
        valid={fieldValid(Field.TEAM)}
        errorMessageKey={withFullTranslationKey('field_required')}
        onFieldChange={onFieldChange}
        forceValidation={forceValidation}
      />
      {fieldValue(Field.TEAM) === OptionsTeam.OTHER && (
        <TextField
          field={{
            name: Field.TEAM_OTHER,
            type: 'text',
            required: true,
            maxlength: 100,
            labelOverride: t(FieldProperties[Field.TEAM_OTHER].label),
          }}
          value={fieldValue(Field.TEAM_OTHER)}
          valid={fieldValid(Field.TEAM_OTHER)}
          onFieldChange={onFieldChange}
          forceValidation={forceValidation}
          key={Field.TEAM_OTHER}
          errorMessageKey={withFullTranslationKey('field_required')}
        />
      )}
      <RadioField
        field={{
          name: Field.URGENCY,
          type: 'text',
          required: true,
          options: [OptionsUrgency.URGENCY_HIGH, OptionsUrgency.URGENCY_MEDIUM, OptionsUrgency.URGENCY_LOW].map(
            withFullTranslationKey,
          ),
          labelOverride: t(FieldProperties[Field.URGENCY].label),
        }}
        value={fieldValue(Field.URGENCY)}
        valid={fieldValid(Field.URGENCY)}
        onFieldChange={onFieldChange}
        forceValidation={forceValidation}
        key={Field.URGENCY}
        errorMessageKey={withFullTranslationKey('field_required')}
      />
      <TextareaField
        field={{
          name: Field.ADDITIONAL_INFO,
          type: 'text',
          maxlength: 20000,
          labelOverride: t(FieldProperties[Field.ADDITIONAL_INFO].label),
          placeholderOverride: t('additional_info_placeholder'),
          // TODO: Now we are forced to use a specific formula for the translation key, perhaps we want to be able to owerwrite his in the future
          help_text: true,
          required: true,
        }}
        value={fieldValue(Field.ADDITIONAL_INFO)}
        onFieldChange={onFieldChange}
        // This is needed for the help text translation to work and is hardcoded in the Base Field
        // ToDo: implement a help text overwrite?
        tPrefix="content_policy_reporting:"
      />
      <ButtonPrimary disabled={loading} onClick={onStepSubmit}>
        {t('submit_report')}
      </ButtonPrimary>
    </Fragment>
  );
};

export default withTranslation('content_policy_reporting')(Step2EmployeeReportedContent);
